import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
	mergeDesignModeSelection,
	resolveStoryDesignModeSelection,
	type DesignDefinition,
	type DesignModeSelection,
	type StoryDesignModeSettings,
} from 'common/design-tokens';

const DesignModeContext = createContext<DesignModeSelection>({});

export function getDesignModeAttributes(selection?: DesignModeSelection): Record<string, string> {
	return Object.fromEntries(
		Object.entries(selection ?? {}).map(([groupId, modeId]) => [`data-sc-mode-${groupId}`, modeId])
	);
}

export function StoryDesignModeProvider({
	design,
	settings,
	children,
}: {
	design: DesignDefinition;
	settings?: StoryDesignModeSettings;
	children: React.ReactNode;
}) {
	const [prefersDark, setPrefersDark] = useState(
		() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
	);

	useEffect(() => {
		// Media activation adapters belong here. Future prefers-contrast or Storycards responsive-platform
		// adapters can extend activation without changing stored ModeValue data or nested inheritance.
		const query = window.matchMedia?.('(prefers-color-scheme: dark)');
		if (!query) return () => {};
		const update = () => setPrefersDark(query.matches);
		query.addEventListener?.('change', update);
		return () => query.removeEventListener?.('change', update);
	}, []);

	const selection = useMemo(
		() => resolveStoryDesignModeSelection(design.modeGroups, settings, { prefersDark }),
		[design, settings, prefersDark]
	);

	useLayoutEffect(() => {
		// An absent semantic controller means the Story still belongs to the legacy dark-mode system.
		// Keep the body untouched so its inversion/toggle behavior remains authoritative.
		if (!settings) return () => {};
		const { body } = document;
		const attributes = getDesignModeAttributes(selection);
		for (const [name, modeId] of Object.entries(attributes)) body.setAttribute(name, modeId);
		const themeMode = design.modeGroups
			.find(group => group.id === 'theme')
			?.modes.find(mode => mode.id === selection.theme);
		const previousColorScheme = body.style.colorScheme;
		body.style.colorScheme = themeMode?.colorScheme ?? '';
		return () => {
			for (const name of Object.keys(attributes)) body.removeAttribute(name);
			body.style.colorScheme = previousColorScheme;
		};
	}, [design.modeGroups, selection, settings]);

	return <DesignModeContext.Provider value={selection}>{children}</DesignModeContext.Provider>;
}

export function DesignModeBoundary({
	selection,
	children,
}: {
	selection?: DesignModeSelection;
	children: React.ReactNode | ((selection: DesignModeSelection) => React.ReactNode);
}) {
	const parent = useContext(DesignModeContext);
	const value = useMemo(() => mergeDesignModeSelection(parent, selection), [parent, selection]);
	return (
		<DesignModeContext.Provider value={value}>
			{typeof children === 'function' ? children(value) : children}
		</DesignModeContext.Provider>
	);
}

export const useDesignModeSelection = () => useContext(DesignModeContext);
